import { useEffect, useRef } from 'react';
import { useModalDrag } from '../hooks/useModalDrag';

/**
 * Confirmation dialog shown before deleting a topic.
 * Props:
 *   topicName  — display name / id of the topic being deleted
 *   onConfirm  — user confirmed deletion
 *   onCancel   — user cancelled
 */
export default function DeleteTopicDialog({ topicName, onConfirm, onCancel }) {
  const { boxRef, onDragStart } = useModalDrag();
  const cancelRef = useRef(null);

  // Focus "Cancel" by default — safer for a destructive action
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div
        ref={boxRef}                              
        onMouseDown={onDragStart}                 
        className="modal-box delete-topic-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dt-title"
      >
        {/* Icon */}
        <div className="dt-icon">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </div>

        <h2 className="modal-title" id="dt-title">Delete topic?</h2>

        <p className="dt-description">
          <strong>"{topicName}"</strong> and all its saved history will be
          permanently removed. This cannot be undone.
        </p>

        <div className="dt-actions">
          <button className="dt-btn dt-btn-danger" onClick={onConfirm}>
            Delete
          </button>
          <button ref={cancelRef} className="dt-btn dt-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
